import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, Check, AlertCircle, Info } from 'lucide-react';
import { user } from '../api';

const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'bs', name: 'Bosanski' },
  { code: 'hr', name: 'Hrvatski' },
  { code: 'sr', name: 'Srpski' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pl', name: 'Polski' },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'ar', name: 'العربية' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' },
];

const WidgetPage = () => {
  const [languageMode, setLanguageMode] = useState('auto');
  const [locale, setLocale] = useState('en');
  const [showCategoryFilter, setShowCategoryFilter] = useState(true);
  const [showGenderFilter, setShowGenderFilter] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await user.getWidgetSettings();
        setLanguageMode(data.languageMode || 'auto');
        setLocale(data.locale || 'en');
        setShowCategoryFilter(data.showCategoryFilter !== false);
        setShowGenderFilter(data.showGenderFilter !== false);
      } catch (err) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await user.updateWidgetSettings({
        languageMode: languageMode === 'manual' ? 'manual' : 'auto',
        locale: languageMode === 'manual' ? locale : undefined,
        showCategoryFilter,
        showGenderFilter,
      });
      setSuccess('Widget settings saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">Widget language</h1>
        <p className="text-white/60">
          Choose how the Virtual Try-On widget language is set for your visitors. No branding is shown.
        </p>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl bg-red-900/20 border border-red-500/20 flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300">{error}</p>
        </motion.div>
      )}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl bg-green-900/20 border border-green-500/20 flex items-center gap-3"
        >
          <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
          <p className="text-green-300">{success}</p>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#111118] rounded-2xl p-6 border border-white/5 mb-6"
      >
        <h2 className="text-lg font-bold text-white mb-4">Language mode</h2>

        <div className="space-y-4">
          <label className="flex items-start gap-4 p-4 rounded-xl bg-[#0a0a0f] border border-white/10 cursor-pointer hover:border-white/20 transition-colors has-[:checked]:border-blue-500/50 has-[:checked]:bg-blue-500/5">
            <input
              type="radio"
              name="languageMode"
              value="auto"
              checked={languageMode === 'auto'}
              onChange={() => setLanguageMode('auto')}
              className="mt-1 w-4 h-4 text-blue-500 border-white/20 focus:ring-blue-500"
            />
            <div className="flex gap-3">
              <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-white">Auto (by visitor location)</span>
                <p className="text-sm text-white/50 mt-1">
                  Detect visitor language from browser and location. Best for international stores.
                </p>
              </div>
            </div>
          </label>

          <label className="flex items-start gap-4 p-4 rounded-xl bg-[#0a0a0f] border border-white/10 cursor-pointer hover:border-white/20 transition-colors has-[:checked]:border-blue-500/50 has-[:checked]:bg-blue-500/5">
            <input
              type="radio"
              name="languageMode"
              value="manual"
              checked={languageMode === 'manual'}
              onChange={() => setLanguageMode('manual')}
              className="mt-1 w-4 h-4 text-blue-500 border-white/20 focus:ring-blue-500"
            />
            <div className="flex gap-3">
              <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-white">Manual</span>
                <p className="text-sm text-white/50 mt-1">
                  Use one language for all visitors. Select the language below.
                </p>
              </div>
            </div>
          </label>
        </div>

        {languageMode === 'manual' && (
          <div className="mt-6 pt-6 border-t border-white/5">
            <label className="block text-sm font-medium text-white/70 mb-2">Widget language</label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full max-w-xs px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white focus:outline-none focus:border-blue-500/50"
            >
              {SUPPORTED_LOCALES.map((loc) => (
                <option key={loc.code} value={loc.code}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-6 p-4 rounded-xl bg-blue-900/20 border border-blue-500/20 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-300/90">
            Translations are applied to widget UI only (buttons, hints, messages). No third-party branding is displayed.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-[#111118] rounded-2xl p-6 border border-white/5 mb-6"
      >
        <h2 className="text-lg font-bold text-white mb-2">Filter visibility (VTO widget)</h2>
        <p className="text-sm text-white/50 mb-4">
          Choose which filter bars appear above the glasses list when customers use the widget with full catalog.
        </p>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-4 rounded-xl bg-[#0a0a0f] border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
            <input
              type="checkbox"
              checked={showCategoryFilter}
              onChange={(e) => setShowCategoryFilter(e.target.checked)}
              className="w-4 h-4 rounded text-blue-500 border-white/20 focus:ring-blue-500"
            />
            <span className="font-medium text-white">Show Category filter (All / Prescription / Sunglasses)</span>
          </label>
          <label className="flex items-center gap-3 p-4 rounded-xl bg-[#0a0a0f] border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
            <input
              type="checkbox"
              checked={showGenderFilter}
              onChange={(e) => setShowGenderFilter(e.target.checked)}
              className="w-4 h-4 rounded text-blue-500 border-white/20 focus:ring-blue-500"
            />
            <span className="font-medium text-white">Show Gender filter (All / Men / Women)</span>
          </label>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Save settings
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
};

export default WidgetPage;
